import { useEffect } from "react";
import { GameContext } from "./GameContextDefinition";
import { useAuth } from "@/hooks/useAuth.js";
import { useSession } from "@/hooks/useSession.js";
import { useTables } from "@/hooks/useTables.js";
import { useCharacters } from "@/hooks/useCharacters.js";
import { useOnlinePlayers } from "@/hooks/useOnlinePlayers.js";

/**
 * Game context provider
 * Manages authentication state, online players and tables
 *
 * @component
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components
 */
export const GameProvider = ({ children }) => {
  // Branding/game type constant (single source of truth)
  const GAME_TYPE = "Guild for Glory"; // Official game name

  // Use specialized hooks for different concerns
  const auth = useAuth();
  const session = useSession();
  const tables = useTables();
  const characters = useCharacters();
  const onlinePlayers = useOnlinePlayers();

  // Load persisted data on mount
  useEffect(() => {
    auth.loadPersistedUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context value with all state and methods
  const value = {
    // Constants
    GAME_TYPE,

    // Auth state and methods
    currentUser: auth.currentUser,
    isAuthenticated: auth.isAuthenticated,
    login: auth.login,
    logout: auth.logout,

    // Session state and methods
    currentSession: session.currentSession,
    sessionKey: session.sessionKey,
    checkSessionConflict: session.checkSessionConflict,
    saveCurrentSession: session.saveCurrentSession,
    clearCurrentSession: session.clearCurrentSession,

    // Online players state and methods
    onlinePlayers: onlinePlayers.onlinePlayers,
    updatePlayerStatus: onlinePlayers.updatePlayerStatus,
    addOnlinePlayer: onlinePlayers.addOnlinePlayer,
    removeOnlinePlayer: onlinePlayers.removeOnlinePlayer,
    hasWebSocketConnection: onlinePlayers.hasWebSocketConnection,

    // Tables state and methods
    activeTables: tables.activeTables,
    createTable: (tableData, onCreated) => {
      console.warn("🎲 [GameContext] createTable called with:", {
        tableData,
        hasCallback: !!onCreated,
      });
      const result = tables.createTable(
        {
          ...tableData,
          creator: auth.currentUser?.username,
        },
        onCreated
      );
      console.warn("🎲 [GameContext] createTable result:", result);
      return result;
    },
    cancelDraftTable: tables.cancelDraftTable,
    publishDraftTable: tables.publishDraftTable,
    joinTable: (tableId, _role) => {
      return tables.joinTable(tableId, auth.currentUser, "player");
    },
    leaveTable: tables.leaveTable,
    spectateTable: (tableId) => {
      return tables.spectateTable(tableId, auth.currentUser);
    },
    startGame: (tableId) => {
      return tables.startGame(tableId, auth.currentUser);
    },
    returnToTable: (tableId) => {
      return tables.returnToTable(tableId, auth.currentUser);
    },

    // Character methods
    playerCharacters: characters.playerCharacters,
    getCharactersForUser: characters.getCharactersForUser,
    addCharacterForCurrentUser: characters.addCharacterForCurrentUser,
    removeCharacterForCurrentUser: characters.removeCharacterForCurrentUser,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
